import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver, clearQueryResult } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
import { getIconFromDriverName } from "./iconFunctions";
import RevenueValueDriverTTMForm from "./RevenueValueDriverTTMForm";
import ModalElement from "../Modal";
import AddProductData from "./AddProductData";
import { toolTipUserIcon } from "./iconFunctions";

const RevenueValueDriverTTM = ({ id, valueModel, products, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();
    
    const [open, setOpen] = React.useState(false);
    const [selectedProduct, setSelectedProduct] = useState(Object.keys(products)[0] || '')
    const [deleteTTMDriverPopupOpen, setDeleteTTMDriverPopupOpen] = useState(false);
    const [deleteTTMDriverId, setDeleteTTMDriverId] = useState(null);
    const [editTTMDriverId, setEditTTMDriverId] = useState(null);
    const [addProductModalOpen, setAddProductModalOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleProductChange = (event) => {
        setSelectedProduct(event.target.value)
        if (editTTMDriverId) {
            setEditTTMDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteTTMDriver = (id) => {
        setDeleteTTMDriverId(id)
        setDeleteTTMDriverPopupOpen(true)
    }

    const editTTMDriver = (id) => {
        setEditTTMDriverId(id)
        let ttmDriver = valueModel.revenue_value_driver.time_to_market.find(ttmDriver => ttmDriver.id === id)
        setSelectedProduct(ttmDriver.product)
    }

    const handleResetTTM = () => {
        setEditTTMDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const toggleAddProductModalOpen = () => {
        setAddProductModalOpen(!addProductModalOpen)
        dispatch(clearQueryResult())
    }

    function renderAddProductModal() {
        return (
            <ModalElement
                modalOpen={addProductModalOpen}
                modalWidth={1000}
                handleClose={toggleAddProductModalOpen}
                header="Add Product Type"
                instruction="Please specify what you would like to add and provide a wish that will bring some context to your request"
                content={
                    <AddProductData
                        id={id}
                        industry={valueModel?.industry}
                        type='ttm'
                        productTypes={products}
                        handleClose={toggleAddProductModalOpen}
                    />
                }
            />

        )
    }

    const onSubmitTTM = async (values, driver, { setSubmitting, setErrors }) => {
        // Clone the values object to avoid direct mutation
        const clonedValues = { ...values };
        clonedValues.id = editTTMDriverId;
        clonedValues.outcome = outcome;
        clonedValues.driver = driver;
        clonedValues.product = selectedProduct;
        clonedValues.units_needed = clonedValues.units_needed.toString().replace(/,/g, "");
        clonedValues.time_needed = clonedValues.time_needed.toString().replace(/,/g, "");

        // values.id = editTTMDriverId
        // values.driver = driver
        // values.product = selectedProduct
        let success = false

        if (editTTMDriverId) {
            const result = await dispatch(editDriver('ttm', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditTTMDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('ttm', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const ttmDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'product', label: 'Type', format: (value) => formatString(value), minWidth: 150, action: false },
        { id: 'time_faster_to_market', label: 'Days to market', minWidth: 100, action: false },
        // { id: 'units_needed', label: 'Units needed', format: (value) => value + ' units', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createTTMDriverData = (id, outcome, driver_name, product, time_faster_to_market, units_needed, positive_driver, actions) => {
        time_faster_to_market = `${time_faster_to_market} days ${positive_driver ? 'faster' : 'slower'}`
        return { id, outcome, driver_name, product, time_faster_to_market, units_needed, positive_driver, actions };
    }

    const ttmDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editTTMDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteTTMDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const ttmDriverRows = valueModel?.revenue_value_driver.time_to_market.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createTTMDriverData(driver.id, driver.outcome, driver.driver_name, driver.product, driver.time_faster_to_market, driver.units_needed, driver.positive_driver, ttmDriverActions(driver.id))
        )
    })

    const ttmDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={ttmDriverColumns}
                    rows={ttmDriverRows}
                    selectedId={editTTMDriverId}
                />
            </React.Fragment>
        )
    }

    const ttmDriverFormActions = () => {
        return (
            <React.Fragment>
                {editTTMDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editTTMDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetTTM}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>

                <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Driver type:
                    </Typography>
                    <Typography variant="caption">
                        Profit Opportunity
                    </Typography>
                </Stack>
                <Box sx={{ width: '30%' }} />
                <Button startIcon={<AutoFixHighIcon />} onClick={toggleAddProductModalOpen} sx={{ textTransform: 'none' }}>Add Product</Button>
            </Box>


            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Product Type
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedProduct}
                        onChange={handleProductChange}
                        size='small'
                    >
                        {Object.keys(products).map((product) => (
                            <MenuItem key={product} value={product}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {formatString(product)}
                                    {products[product]?.user_created && toolTipUserIcon()}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <RevenueValueDriverTTMForm
                currency="€"
                ownProduct={valueModel?.product}
                product={products[selectedProduct]}
                editTTMDriverId={editTTMDriverId}
                editTTMDriver={valueModel?.revenue_value_driver.time_to_market.find(ttmDriver => ttmDriver.id === editTTMDriverId)}
                formActions={ttmDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitTTM(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {ttmDriverTable()}
            <DeletePopup open={deleteTTMDriverPopupOpen} onClose={() => setDeleteTTMDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('ttm', id, deleteTTMDriverId))} />
            {renderAddProductModal()}
        </Box>
    )

}

export default RevenueValueDriverTTM